import { apiDelete, apiGet, apiPatch, apiPost } from './client.js';

export interface TicketListParams {
  status?: string;
  priority?: string;
  assignedTo?: string;
  assignedToMe?: boolean;
  customerId?: string;
  modelId?: string;
  customModelName?: string;
  storeId?: string;
  search?: string;
  source?: string;
  excludeSource?: string;
  /** `repairdesk` = RepairDesk imports; `native` = Repairiz-created only */
  importedFrom?: 'repairdesk' | 'native';
  overdue?: boolean;
  overdueOnly?: boolean;
  warrantyOnly?: boolean;
  dateFrom?: string;
  dateTo?: string;
  createdFrom?: string;
  createdTo?: string;
  dueFrom?: string;
  dueTo?: string;
  sort?: 'priority' | 'newest' | 'oldest' | 'due_date' | 'customer';
  page?: number;
  pageSize?: number;
}

export interface TicketListItem {
  id: string;
  ticketNumber: string;
  status: string;
  priority: string;
  source?: string;
  isWarranty?: boolean;
  parentTicketNumber?: string | null;
  importedFrom?: string | null;
  externalId?: string | null;
  customerId?: string | null;
  customerDisplayName?: string;
  customerPhone?: string | null;
  customerNameOverride?: string | null;
  customerStatus?: string | null;
  createdAt: string;
  promisedDate?: string | null;
  warrantyType?: 'days' | 'lifetime' | 'none' | null;
  warrantyDays?: number | null;
  warrantyExpiresAt?: string | null;
  total?: string;
  balanceDue?: string;
  depositRequired?: boolean;
  depositAmount?: string;
  depositCollected?: string;
  deviceSummary?: string;
  deviceCount?: number;
  repairSummary?: string;
  lineItemSummary?: string;
  repairs?: string[];
  assignedTechName?: string | null;
  customerComplaint?: string | null;
}

export type TicketCounts = Record<string, number>;

export type PredictiveAlertSeverity = 'high' | 'medium' | 'low';

export interface PredictiveAlert {
  type: string;
  severity: PredictiveAlertSeverity;
  title: string;
  message: string;
  suggestion: string;
  action: string;
  data?: Record<string, unknown>;
}

export interface TicketDetail {
  ticket: TicketRecord;
  customer?: TicketCustomer | null;
  devices: TicketDevice[];
  repairs: TicketRepair[];
  notes: TicketNote[];
  lineItems: TicketLineItem[];
  payments: TicketPayment[];
  timeEntries: TicketTimeEntry[];
  timeEntriesSummary?: {
    totalSeconds: number;
    billableSeconds: number;
    nonBillableSeconds: number;
  };
  recentEvents: TicketEvent[];
  communications?: TicketCommunication[];
  suppliedItems?: SuppliedItem[];
  estimatedProfit?: string | null;
  partsCost?: string | null;
}

export type ConditionStatus = 'working' | 'not_working' | 'not_tested' | 'na';

export interface DeviceConditionItem {
  name: string;
  status: ConditionStatus;
}

export interface DevicePhoto {
  url: string;
  caption?: string;
  uploadedAt: string;
}

export interface SuppliedItem {
  name: string;
  checked: boolean;
}

export interface TicketCommunication {
  id: string;
  channel: string;
  direction: string;
  toAddress: string;
  fromAddress?: string | null;
  subject?: string | null;
  body?: string | null;
  status: string;
  dryRun?: boolean;
  createdAt: string;
  sentAt?: string | null;
  deliveredAt?: string | null;
}

export interface TicketTimeEntry {
  id: string;
  ticketId: string;
  ticketRepairId: string | null;
  repairServiceName: string | null;
  userId: string;
  userFirstName: string | null;
  userLastName: string | null;
  entryType: string;
  startedAt: string | null;
  endedAt: string | null;
  durationSeconds: number | null;
  billable: boolean;
  note: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TicketCustomer {
  id: string;
  firstName: string;
  lastName?: string | null;
  phone?: string | null;
  email?: string | null;
  pointsBalance?: number | string | null;
  loyaltyPointsBalance?: number | string | null;
  storeCreditBalance?: string | null;
  totalTickets?: number | null;
  totalSpend?: string | null;
  customerStatus?: string | null;
  statusReason?: string | null;
}

export interface TicketRecord {
  id: string;
  ticketNumber: string;
  status: string;
  priority: string;
  customerId?: string | null;
  customerNameOverride?: string | null;
  customerPhoneOverride?: string | null;
  customerEmailOverride?: string | null;
  source?: string;
  subtotal: string;
  taxTotal: string;
  total: string;
  paidTotal: string;
  depositRequired?: boolean;
  depositAmount?: string;
  depositCollected?: string;
  depositSkipped?: boolean;
  balanceDue: string;
  discountTotal?: string;
  partsCostTotal?: string;
  partsRetailTotal?: string;
  partsTaxTotal?: string;
  promisedDate?: string | null;
  warrantyType?: 'days' | 'lifetime' | 'none' | null;
  warrantyDays?: number | null;
  warrantyExpiresAt?: string | null;
  warrantyOfTicketId?: string | null;
  portalToken?: string | null;
  createdAt: string;
  updatedAt: string;
  preWaiverRequired?: boolean;
  preWaiverSignedAt?: string | null;
  preWaiverSignatureUrl?: string | null;
  preWaiverVersion?: string | null;
  preWaiverSkipped?: boolean;
  postWaiverSignedAt?: string | null;
  postWaiverSignatureUrl?: string | null;
  postWaiverVersion?: string | null;
  assignedTo?: string | null;
  assigneeFirstName?: string | null;
  assigneeLastName?: string | null;
  createdBy?: string | null;
  createdByFirstName?: string | null;
  createdByLastName?: string | null;
  statusOverride?: boolean;
  tags?: string[] | null;
  intakeNotes?: string | null;
  customerComplaint?: string | null;
  originalStoreId?: string | null;
  originalStoreName?: string | null;
  transferredAt?: string | null;
  transferredFromTicketId?: string | null;
  clonedFromTicketId?: string | null;
  clonedFrom?: { id: string; ticketNumber: string } | null;
  isWarranty?: boolean;
  parentTicketId?: string | null;
  parentTicket?: { id: string; ticketNumber: string } | null;
  warrantyReason?: string | null;
  warrantyTickets?: Array<{ id: string; ticketNumber: string; status: string; createdAt: string }>;
  importedFrom?: string | null;
  externalId?: string | null;
}

export interface TicketDevice {
  id: string;
  makeId?: string | null;
  modelId?: string | null;
  makeName?: string | null;
  modelName?: string | null;
  customModelName?: string | null;
  imei?: string | null;
  imei2?: string | null;
  serialNumber?: string | null;
  color?: string | null;
  storageGb?: number | string | null;
  category?: string | null;
  conditionAtIntake: string | null;
  conditionNotes?: string | null;
  passcodeType?: string | null;
  accessoriesReceived?: string[] | null;
  preConditions?: DeviceConditionItem[];
  postConditions?: DeviceConditionItem[];
  photosPre?: DevicePhoto[];
  photosPost?: DevicePhoto[];
  workStatus?: 'pending' | 'in_repair' | 'waiting_parts' | 'repaired';
  hasPasscode?: boolean;
}

export interface TicketRepair {
  id: string;
  ticketDeviceId?: string;
  serviceId?: string | null;
  serviceName: string;
  quotedPrice: string;
  actualPrice?: string | null;
  status: string;
  assignedTechId?: string | null;
  assignedTechFirstName?: string | null;
  assignedTechLastName?: string | null;
  categorySlug?: string | null;
  isWarrantyWork?: boolean;
}

export interface TicketNote {
  id: string;
  userId?: string | null;
  visibility: string;
  body: string;
  attachments?: Array<{ url: string; filename?: string; mime_type?: string }> | null;
  createdAt: string;
}

export interface TicketLineItem {
  id: string;
  ticketRepairId?: string | null;
  description: string;
  type: string;
  quantity: string;
  unitPrice: string;
  discountAmount?: string | null;
  discountPct?: string | null;
  discountReason?: string | null;
  taxAmount?: string | null;
  lineTotal: string;
}

export interface TicketPayment {
  id: string;
  amount: string;
  method: string;
  status: string;
  cashTendered?: string | null;
  changeDue?: string | null;
  transactionId?: string | null;
  cardType?: string | null;
  lastFour?: string | null;
  processedManually?: boolean | null;
  cashDiscountApplied?: boolean | null;
  cashDiscountAmount?: string | null;
  processedBy?: string | null;
  notes?: string | null;
  createdAt: string;
  blockyypTransactionId?: string | null;
  cardBrand?: string | null;
  cardLast4?: string | null;
  voidedAt?: string | null;
  voidReason?: string | null;
  editedAt?: string | null;
  editNotes?: string | null;
  originalAmount?: string | null;
  originalMethod?: string | null;
  recordedBy?: { id: string; firstName: string; lastName: string } | null;
  voidedBy?: { id: string; firstName: string; lastName: string } | null;
  editedBy?: { id: string; firstName: string; lastName: string } | null;
}

export interface TicketLabel {
  ticketNumber: string;
  barcode: string;
  customerName: string;
  customerPhone: string;
  deviceSummary: string;
  repairSummary: string;
  intakeDate: string;
  promisedDate: string | null;
  technicianName: string;
  storePhone: string;
  storeName: string;
}

export interface TicketEvent {
  id: string;
  eventType: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export type TicketWaiverType = 'pre_repair' | 'post_repair';

export type WaiverCollectionMode = 'hand_across' | 'customer_display' | 'sms_link';

export interface TicketWaiverDocument {
  content: string;
  version: string;
  mergeFields: Record<string, string>;
}

export interface TicketWaiverSignedRecord {
  ticketId: string;
  ticketNumber: string;
  storeName: string;
  storePhone: string | null;
  storeAddress: string | null;
  customerName: string;
  customerPhone: string | null;
  waiverType: TicketWaiverType;
  waiverTitle: string;
  waiverVersion: string;
  signedAt: string;
  waiverContent: string;
  signatureDataUrl: string;
  mergeFields: Record<string, string>;
  devices: { label: string; repairs: { name: string; price: string }[] }[];
  subtotal: string;
  discountTotal: string;
  taxTotal: string;
  total: string;
}

export function getTicketWaiverRecord(ticketId: string, waiverType: TicketWaiverType) {
  return apiGet<TicketWaiverSignedRecord>(`/api/v1/tickets/${ticketId}/waiver/${waiverType}/record`).then(
    (res) => res.data,
  );
}

export interface PaginatedMeta {
  page: number;
  pageSize: number;
  total: number;
}

export function listTickets(params?: TicketListParams) {
  return apiGet<TicketListItem[], PaginatedMeta>('/api/v1/tickets', { params });
}

export function getTicketCounts() {
  return apiGet<TicketCounts>('/api/v1/tickets/counts').then((res) => res.data);
}

export type PromisedTimeSuggestion = {
  suggestedAt: string;
  confidenceLevel: 'high' | 'medium' | 'low';
  reasoning: string;
  breakdown: {
    repairMinutes: number;
    queueWaitMinutes: number;
    bufferMinutes: number;
    totalMinutes: number;
  };
  alternatives: Array<{
    label: string;
    suggestedAt: string;
    totalMinutes: number;
  }>;
};

export function getPromisedTime(params?: {
  serviceIds?: string[];
  assignedToId?: string;
  estimatedMinutes?: number;
}) {
  const qs = new URLSearchParams();
  if (params?.serviceIds?.length) qs.set('serviceIds', params.serviceIds.join(','));
  if (params?.assignedToId) qs.set('assignedToId', params.assignedToId);
  if (params?.estimatedMinutes) qs.set('estimatedMinutes', String(params.estimatedMinutes));
  const q = qs.toString();
  return apiGet<PromisedTimeSuggestion>(`/api/v1/tickets/promised-time${q ? `?${q}` : ''}`);
}

export function recalculatePromisedTime(ticketId: string) {
  return apiPost<{
    promisedDate: string | null;
    suggestion: PromisedTimeSuggestion;
  }>(`/api/v1/tickets/${ticketId}/promised-time/recalculate`);
}

export function bulkUpdateTickets(body: {
  ticketIds: string[];
  status?: string;
  assignedTo?: string | null;
}) {
  return apiPost<{ updated: number; errors: Array<{ ticketId: string; code: string; message: string }> }>(
    '/api/v1/tickets/bulk-update',
    body,
  ).then((res) => res.data);
}

export function getTicket(id: string) {
  return apiGet<TicketDetail>(`/api/v1/tickets/${id}`).then((res) => res.data);
}

export function getTicketLabel(id: string) {
  return apiGet<TicketLabel>(`/api/v1/tickets/${id}/label`).then((res) => res.data);
}

export interface CollectDepositInput {
  amount: number;
  method: 'cash' | 'card' | 'store_credit';
  cashTendered?: number;
  notes?: string;
}

export interface CollectDepositResult {
  ticket: TicketRecord;
  payment: TicketPayment;
  cashSessionUpdated?: boolean;
  cashSessionWarning?: string | null;
}

export function collectTicketDeposit(ticketId: string, body: CollectDepositInput) {
  return apiPost<CollectDepositResult, CollectDepositInput>(
    `/api/v1/tickets/${ticketId}/deposit`,
    body,
  ).then((res) => res.data);
}

export interface TicketCreateResult extends TicketDetail {
  predictiveAlerts?: PredictiveAlert[];
}

export function createTicket(body: Record<string, unknown>) {
  return apiPost<TicketCreateResult, Record<string, unknown>>('/api/v1/tickets', body).then(
    (res) => res.data,
  );
}

export function updateTicket(id: string, body: Record<string, unknown>) {
  return apiPatch<TicketRecord, Record<string, unknown>>(`/api/v1/tickets/${id}`, body).then(
    (res) => res.data,
  );
}

export function updateTicketStatus(id: string, body: Record<string, unknown>) {
  return apiPost<
    TicketRecord,
    Record<string, unknown>
  >(`/api/v1/tickets/${id}/status`, body).then((res) => {
    const envelope = res as typeof res & {
      meta?: {
        startedTimer?: {
          id: string;
          startedAt: string;
          ticketRepairId: string | null;
        };
      };
    };
    return {
      ticket: envelope.data,
      startedTimer: envelope.meta?.startedTimer,
    };
  });
}

export function deleteTicket(
  id: string,
  body: {
    reason: string;
    confirmTicketNumber: string;
    managerOverrideAuthorizedBy: string;
    acknowledgePayments?: boolean;
  },
) {
  return apiDelete<{ deleted: boolean; ticketNumber: string; reason: string }>(
    `/api/v1/tickets/${id}`,
    { data: body },
  ).then((res) => res.data);
}

export function bulkDeleteTickets(body: {
  ticketIds: string[];
  reason: string;
  confirmText: 'DELETE';
  managerOverrideAuthorizedBy: string;
}) {
  return apiPost<
    {
      deletedCount: number;
      deletedTicketNumbers: string[];
      errors: Array<{ ticketId: string; code: string; message: string }>;
    },
    typeof body
  >('/api/v1/tickets/bulk-delete', body).then((res) => res.data);
}

export function addNote(id: string, body: Record<string, unknown>) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/tickets/${id}/notes`,
    body,
  ).then((res) => res.data);
}

export function assignTicket(id: string, body: Record<string, unknown>) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/tickets/${id}/assign`,
    body,
  ).then((res) => res.data);
}

export function addLineItem(id: string, body: Record<string, unknown>) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/tickets/${id}/line-items`,
    body,
  ).then((res) => res.data);
}

export function updateLineItem(id: string, lineId: string, body: Record<string, unknown>) {
  return apiPatch<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/tickets/${id}/line-items/${lineId}`,
    body,
  ).then((res) => res.data);
}

export function deleteLineItem(id: string, lineId: string) {
  return apiDelete<Record<string, unknown>>(`/api/v1/tickets/${id}/line-items/${lineId}`).then(
    (res) => res.data,
  );
}

export function addRepair(id: string, body: Record<string, unknown>) {
  return apiPost<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/tickets/${id}/repairs`,
    body,
  ).then((res) => res.data);
}

export function updateRepair(id: string, repairId: string, body: Record<string, unknown>) {
  return apiPatch<Record<string, unknown>, Record<string, unknown>>(
    `/api/v1/tickets/${id}/repairs/${repairId}`,
    body,
  ).then((res) => res.data);
}

export function deleteRepair(id: string, repairId: string) {
  return apiDelete<Record<string, unknown>>(`/api/v1/tickets/${id}/repairs/${repairId}`).then(
    (res) => res.data,
  );
}

export function createTimeEntry(ticketId: string, body: Record<string, unknown>) {
  return apiPost<TicketTimeEntry, Record<string, unknown>>(
    `/api/v1/tickets/${ticketId}/time-entries`,
    body,
  ).then((res) => res.data);
}

export function patchTimeEntry(ticketId: string, entryId: string, body: Record<string, unknown>) {
  return apiPatch<TicketTimeEntry, Record<string, unknown>>(
    `/api/v1/tickets/${ticketId}/time-entries/${entryId}`,
    body,
  ).then((res) => res.data);
}

export function deleteTimeEntry(ticketId: string, entryId: string) {
  return apiDelete<Record<string, never>>(`/api/v1/tickets/${ticketId}/time-entries/${entryId}`).then(
    () => undefined,
  );
}

export function listTimeEntries(ticketId: string) {
  return apiGet<{ entries: TicketTimeEntry[]; summary: TicketDetail['timeEntriesSummary'] }>(
    `/api/v1/tickets/${ticketId}/time-entries`,
  ).then((res) => res.data);
}

export function getTicketWaiver(ticketId: string, waiverType: TicketWaiverType) {
  return apiGet<TicketWaiverDocument>(`/api/v1/tickets/${ticketId}/waiver/${waiverType}`).then(
    (res) => res.data,
  );
}

export function signTicketWaiver(
  ticketId: string,
  body: {
    waiverType: TicketWaiverType;
    signatureDataUrl: string;
    waiverVersion: string;
    collectionMode: WaiverCollectionMode;
  },
) {
  return apiPost<{ ticket: TicketRecord }, typeof body>(
    `/api/v1/tickets/${ticketId}/waiver/sign`,
    body,
  ).then((res) => res.data);
}

export type DevicePriceChange = {
  repairId: string;
  serviceName: string;
  previousPrice: string;
  newPrice: string;
  applied: boolean;
};

export type PatchTicketDeviceBody = {
  preConditions?: DeviceConditionItem[];
  postConditions?: DeviceConditionItem[];
  photosPre?: DevicePhoto[];
  photosPost?: DevicePhoto[];
  imei?: string | null;
  imei2?: string | null;
  serialNumber?: string | null;
  makeId?: string | null;
  modelId?: string | null;
  customModelName?: string | null;
  color?: string | null;
  conditionAtIntake?: string;
  conditionNotes?: string | null;
  passcodeType?: string;
  passcode?: string | null;
  updateRepairPrices?: boolean;
};

export type PatchTicketDeviceResult = {
  device: TicketDevice;
  priceChanges: DevicePriceChange[];
  partsWarning: string | null;
};

export function patchTicketDevice(ticketId: string, deviceId: string, body: PatchTicketDeviceBody) {
  return apiPatch<PatchTicketDeviceResult | TicketDevice, PatchTicketDeviceBody>(
    `/api/v1/tickets/${ticketId}/devices/${deviceId}`,
    body,
  ).then((res) => {
    const d = res.data as PatchTicketDeviceResult | TicketDevice;
    if (d && typeof d === 'object' && 'device' in d) {
      return d as PatchTicketDeviceResult;
    }
    return {
      device: d as TicketDevice,
      priceChanges: [] as DevicePriceChange[],
      partsWarning: null as string | null,
    };
  });
}

export function deleteTicketDevice(
  ticketId: string,
  deviceId: string,
  body?: { reassignToDeviceId?: string },
) {
  return apiDelete<{
    deleted: boolean;
    deviceId: string;
    wasLastDevice: boolean;
    reassignedRepairCount: number;
  }>(`/api/v1/tickets/${ticketId}/devices/${deviceId}`, body ? { data: body } : undefined).then(
    (res) => res.data,
  );
}

export function patchSuppliedItems(ticketId: string, suppliedItems: SuppliedItem[]) {
  return apiPatch<{ suppliedItems: SuppliedItem[] }, { suppliedItems: SuppliedItem[] }>(
    `/api/v1/tickets/${ticketId}/supplied-items`,
    { suppliedItems },
  ).then((res) => res.data);
}

export function uploadDevicePhoto(
  ticketId: string,
  deviceId: string,
  body: { photoType: 'pre' | 'post'; dataUrl: string; caption?: string },
) {
  return apiPost<TicketDevice, typeof body>(
    `/api/v1/tickets/${ticketId}/devices/${deviceId}/photos`,
    body,
  ).then((res) => res.data);
}

export interface BatchPhotoResult {
  uploaded: number;
  photos: Array<{ url: string; caption: string; uploadedAt: string }>;
}

export function uploadTicketPhotosBatch(
  ticketId: string,
  body: {
    deviceId?: string;
    photoType: 'pre' | 'post';
    photos: Array<{ dataUrl: string; caption?: string }>;
  },
) {
  return apiPost<BatchPhotoResult, typeof body>(
    `/api/v1/tickets/${ticketId}/photos/batch`,
    body,
  ).then((res) => res.data);
}

export function deleteDevicePhoto(
  ticketId: string,
  deviceId: string,
  photoIndex: number,
  photoType: 'pre' | 'post',
) {
  return apiDelete<TicketDevice>(
    `/api/v1/tickets/${ticketId}/devices/${deviceId}/photos/${photoIndex}?photoType=${photoType}`,
  ).then((res) => res.data);
}

export function sendTicketSms(ticketId: string, body: { message: string; toPhone?: string }) {
  return apiPost<{ success: boolean; messageId: string | null; status: string }, typeof body>(
    `/api/v1/tickets/${ticketId}/sms`,
    body,
  ).then((res) => res.data);
}

export function sendNpsSurveyNow(ticketId: string) {
  return apiPost<{ surveyId: string; created: boolean; dryRun: boolean }>(
    `/api/v1/tickets/${ticketId}/send-nps-now`,
    {},
  ).then((r) => r.data);
}

export function sendReviewRequestNow(ticketId: string) {
  return apiPost<{ reviewRequestId: string; created: boolean; dryRun: boolean }>(
    `/api/v1/tickets/${ticketId}/send-review-now`,
    {},
  ).then((r) => r.data);
}

export function sendTicketEmail(
  ticketId: string,
  body: { to: string; subject: string; body: string },
) {
  return apiPost<{ success: boolean; commsLogId?: string; status: string }, typeof body>(
    `/api/v1/tickets/${ticketId}/email`,
    body,
  ).then((res) => res.data);
}

export interface TransferTicketInput {
  destinationStoreId: string;
  reason?: string;
  notifyCustomer?: boolean;
}

export interface TransferTicketResult {
  ticket: TicketRecord;
  newTicketId?: string;
  newTicketNumber?: string;
}

export function transferTicket(ticketId: string, body: TransferTicketInput) {
  return apiPost<TransferTicketResult, TransferTicketInput>(
    `/api/v1/tickets/${ticketId}/transfer`,
    body,
  ).then((res) => res.data);
}

export type CloneTicketInput = {
  customerId?: string | null;
  includeRepairs?: boolean;
  includeDevices?: boolean;
  includeNotes?: boolean;
  intakeType?: 'walk_in' | 'drop_off' | 'quote';
  notes?: string | null;
};

export function cloneTicket(ticketId: string, body: CloneTicketInput = {}) {
  return apiPost<TicketRecord, CloneTicketInput>(`/api/v1/tickets/${ticketId}/clone`, body).then(
    (res) => res.data,
  );
}
